<template>
  <div v-rect-loading="loading" class="company-detail-container">
    <div class="company-detail-header">
      <div class="company-detail-title">{{ $t(`${i18nPrefix}.title.companyDetailTitle`) }}</div>
    </div>
    <div class="company-detail-content">
      <div class="operation-btn-container">
        <el-button class="edit" @click="handleEdit">{{ $t('common.btn.edit') }}</el-button>
        <el-button class="premium-setup" @click="handlePremiumSetup">{{ $t(`${i18nPrefix}.btn.premiumSetup`) }}</el-button>
      </div>
      <div class="head-info">
        <div class="company-img-container">
          <div class="web-logo">
            <div class="web-logo-frame">
              <img class="web-img" :src="company.logo ? $DjangoBaseUrl(company.logo) : $DjangoStaticUrl('images_en/default_company.jpg')" />
            </div>
            <label>{{ $t(`${i18nPrefix}.label.onWeb`) }}</label>
          </div>
          <div class="app-logo">
            <img class="app-img" :src="company.logo ? $DjangoBaseUrl(company.logo) : $DjangoStaticUrl('images_en/default_company.jpg')" />
            <label>{{ $t(`${i18nPrefix}.label.onApp`) }}</label>
          </div>
        </div>
        <div class="company-info">
          <span class="company-name">{{ company.i18nName }}</span>
          <span class="status" :class="`${COMPANY_STATUS_MAP[company.status]}-status`">{{ $t(`${i18nPrefix}.label.${COMPANY_STATUS_MAP[company.status]}`) }}</span>
        </div>
      </div>
      <div class="content-container">
        <div class="profile-info">
          <InfoCard
            class="basic-info item-info-left"
            icon="iconfont icona-BasicInfo"
            :title="$t(`${i18nPrefix}.label.basicInfo`)"
            :infoList="infoList"
            :infoLines="basicInfoLines"
          >
            <template #description>
              <div v-html="company.i18nDescription || '-' " class="description" ></div>
            </template>
          </InfoCard>
          <InfoCard
            class="business-license item-info-left"
            icon="iconfont icona-BasicInfo"
            :title="$t(`${i18nPrefix}.label.businessLicense`)"
            :infoList="infoList"
            :infoLines="licenseInfoLines"
          >
            <template #licensePhoto>
              <div v-if="company.license_photo" class="business-license-row">
                <a rel='gal1' :title="$t(`${i18nPrefix}.label.businessLicense`)" :href="$DjangoBaseUrl(company.license_photo)">
                  <img class="business-license-img" :src="$DjangoBaseUrl(company.license_photo)" :title="$t(`${i18nPrefix}.label.businessLicense`)">
                </a>
              </div>
            </template>
          </InfoCard>
        </div>
        <div class="profile-info">
          <InfoCard
            class="contact-info item-info-right"
            icon="iconfont iconExperience"
            :title="$t(`${i18nPrefix}.label.contactInformation`)"
            :infoList="infoList"
            :infoLines="contactInfoLines"
          >
            <template #website>
              <a v-if="company.website" target="_blank" :href="companyWebsite">{{ company.website || '-' }}</a>
            </template>
            <template #creator>
              <div>{{ownerAccount.name || '-' }}</div>
            </template>
            <template #creatorEmailAddress>
              <a :href="`mailto:${ownerAccount.email_address || ''}`">{{ ownerAccount.email_address || '' }}</a>
            </template>
          </InfoCard>
        </div>
      </div>
      <div class="bottom-btn-group">
        <el-button class="back-btn" @click="handleCancel">{{ $t('common.btn.back') }}</el-button>
        <div>
            <el-button v-for="item in statusKeys" class="status-btn" :key="item" @click="handleStatus(item)">{{ $t(`${i18nPrefix}.btn.${COMPANY_STATUS_MAP[item]}`) }}</el-button>
        </div>
      </div>
    </div>
    <DialogWrapper
      v-if="statusChangeDialog.show"
      class="status-change-dialog"
      cancelBtnLabelI18nKey="common.no"
      saveBtnLabelI18nKey="common.yes"
      :closeOnClickModal="false"
      :title="statusChangeDialog.title"
      :loading="statusChangeDialog.loading"
      :show.sync="statusChangeDialog.show"
      @save="handleCompanyStatus"
    />
    <DialogWrapper
      v-if="purgeDataDialog.show"
      class="purge-data-dialog"
      cancelBtnLabelI18nKey="common.no"
      saveBtnLabelI18nKey="common.yes"
      :closeOnClickModal="false"
      :title="purgeDataDialog.title"
      :loading="purgeDataDialog.loading"
      :show.sync="purgeDataDialog.show"
      @cancel="handlePurgeData(false)"
      @save="handlePurgeData(true)"
    />
  </div>
</template>
<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { addI18nProperty } from '@/i18n'
import { mixedRouterPush } from '@/router/routes'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import moment from 'moment'
import { COMPANY_STATUS, COMPANY_STATUS_MAP } from '@/constant/company.js'
import InfoCard from './components/infoCard.vue'

export default {
  components: {
    DialogWrapper,
    InfoCard
  },
  data () {
    return {
      COMPANY_STATUS_MAP: COMPANY_STATUS_MAP,
      i18nPrefix: 'admin.company.companyApprovalManagement',
      loading: false,
      company: {},
      ownerAccount: {},
      companyPremium: {},
      statusChangeDialog: {
        show: false,
        loading: false,
        title: null,
        content: null,
        status: null
      },
      purgeDataDialog: {
        show: false,
        loading: false,
        title: null,
        content: null,
        purgeData: false
      }
    }
  },
  computed: {
    companyId () {
      return this.$route.params.companyId
    },
    companyWebsite () {
      const website = this.company.website || ''
      if (!/^(https?:\/\/)/i.test(website)) {
        return `http://${website}`
      }
      return website
    },
    statusKeys () {
      const keys = _.keys(COMPANY_STATUS_MAP)
      return _.filter(keys, key => key !== this.company.status)
    },
    infoList () {
      return [this.company]
    },
    basicInfoLines () {
      const i18nPrefix = this.i18nPrefix
      const lines = {
        worldCity: {
          labelI18nKey: `${i18nPrefix}.label.cityOrTown`,
          formatter: (val, info) => info.world_city ? info.world_city.i18nName : ''
        },
        primaryIndustry: {
          labelI18nKey: `${i18nPrefix}.label.industry`,
          formatter: (val, info) => info.primary_industry ? info.primary_industry.i18nName : ''
        },
        size: {
          labelI18nKey: `${i18nPrefix}.label.employeesInGreaterChina`
        },
        description: {
          labelI18nKey: `${i18nPrefix}.label.companyDescription`,
          contentName: 'description',
          fullLine: true
        }
      }
      return lines
    },
    contactInfoLines () {
      const i18nPrefix = this.i18nPrefix
      const lines = {
        contact_name: {
          labelI18nKey: `${i18nPrefix}.label.contactPerson`
        },
        contact_mobile: {
          labelI18nKey: `${i18nPrefix}.label.contactPhone`
        },
        website: {
          labelI18nKey: `${i18nPrefix}.label.companyWebsite`,
          contentName: 'website'
        },
        creator: {
          labelI18nKey: `${i18nPrefix}.label.companyCreator`,
          multipleText: true,
          contentName: 'creator'
        },
        creatorEmailAddress: {
          labelI18nKey: `${i18nPrefix}.label.companyCreatorEmail`,
          multipleText: true,
          contentName: 'creatorEmailAddress'
        }
      }
      return lines
    },
    licenseInfoLines () {
      const i18nPrefix = this.i18nPrefix
      const lines = {
        license_number: {
          labelI18nKey: `${i18nPrefix}.label.businessLicenseNumber`
        },
        license_photo: {
          labelI18nKey: `${i18nPrefix}.label.businessLicense`,
          contentName: 'licensePhoto',
          fullLine: true
        }
      }
      return lines
    }

  },
  created () {
    this.initPage()
  },
  methods: {
    ...mapActions('companyManagement', ['getCompanyApprovalInfo', 'updateCompanyApprovalStatus']),
    async initPage () {
      this.loadCompanyApprovalInfo()
    },
    async loadCompanyApprovalInfo () {
      if (!this.companyId) {
        return
      }

      this.loading = true
      try {
        const resp = await this.getCompanyApprovalInfo(this.companyId)
        if (resp) {
          if (resp.company) {
            resp.company.name = resp.company.name || resp.company.cn_name
            resp.company.cn_name = resp.company.cn_name || resp.company.name
            addI18nProperty(resp.company, 'i18nName', 'cn_name', 'name')
            addI18nProperty(resp.company, 'i18nDescription', 'cn_description', 'description')
            if (resp.company && resp.company.primary_industry) {
              addI18nProperty(resp.company.primary_industry, 'i18nName', 'cn_name', 'name')
            }
            if (resp.company && resp.company.world_city) {
              addI18nProperty(resp.company.world_city, 'i18nName', 'cn_name', 'name')
            }
            this.company = resp.company
          }

          this.ownerAccount = resp.owner_account || {}
          this.companyPremium = resp.company_premium || {}
        }
      } finally {
        this.loading = false
      }
    },
    formatPremiumExpireDate (date) {
      return date ? moment(date).format('YYYY-MM-DD') : ''
    },
    formatPremiumExpireMonthDay (date) {
      return date ? moment(date).format('MM-DD') : ''
    },
    handleEdit () {
      this.$router.push({ path: `/v-admin/client-management/company/${this.companyId}/edit/`, query: this.$route.query })
    },
    handlePremiumSetup () {
      this.$router.push({ path: `/v-admin/client-management/company/${this.companyId}/premium/`, query: this.$route.query })
    },
    handleCancel () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        this.$router.push({ path: '/v-admin/client-management/company/list/', query: this.$route.query })
      }
    },
    handleStatus (status) {
      this.changeStatusDialogData(false, status)
    },
    changeStatusDialogData (init, status) {
      if (init) {
        this.statusChangeDialog.loading = false
        this.statusChangeDialog.show = false
        this.statusChangeDialog.row = null
        this.statusChangeDialog.status = null
      } else {
        const statusTip = this.$t(`${this.i18nPrefix}.label.${COMPANY_STATUS_MAP[status]}`)
        const title = this.$t(`${this.i18nPrefix}.label.companyStatusTip`, { status: statusTip })
        this.statusChangeDialog.title = title
        this.statusChangeDialog.status = status
        this.statusChangeDialog.show = true
      }
    },
    purgeDataDialogData (init) {
      if (init) {
        this.purgeDataDialog.loading = false
        this.purgeDataDialog.show = false
        this.purgeDataDialog.purgeData = false
      } else {
        this.purgeDataDialog.title = this.$t(`${this.i18nPrefix}.label.purgeDataTip`)
        this.purgeDataDialog.show = true
      }
    },
    handleCompanyStatus () {
      const status = this.statusChangeDialog.status
      if (status === COMPANY_STATUS.CLOSED && this.company.is_root_company) {
        this.purgeDataDialogData(false)
      } else {
        this.updateCompanyStatus()
      }
    },
    handlePurgeData (purgeData) {
      this.purgeDataDialog.purgeData = purgeData
      this.purgeDataDialogData(true)
      this.updateCompanyStatus()
    },
    async updateCompanyStatus () {
      const status = this.statusChangeDialog.status
      this.statusChangeDialog.loading = true
      try {
        const data = {
          company_id: this.company.id,
          from_status: this.company.status,
          to_status: status
        }
        if (this.purgeDataDialog.purgeData) {
          data.purge_data = true
        }
        await this.updateCompanyApprovalStatus(data)
        this.loadCompanyApprovalInfo()
      } finally {
        this.changeStatusDialogData(true)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.company-detail-container {

  .company-detail-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;

    .company-detail-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .company-detail-content {
    margin: 30px auto 0;
    font-size: 14px;
    font-weight: 500;

   .operation-btn-container {
      display: flex;
      justify-content: flex-end;

      .el-button {
        padding: 0px 20px;
        border-radius: 2px;
        height: 28px;
        font-size: 14px;
        font-weight: 500;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }

      .premium-setup {
        margin-left: 20px;
      }
    }

    .head-info {
      display: flex;
      .company-img-container {
        display: flex;
        .web-logo {
          width: 155px;
          .web-logo-frame {
            display: flex;
            height: 70px;
            border: 1px solid #ededed;
            justify-content: center;
            align-items: center;
            .web-img {
              max-width: 132px;
              height: 60px;
            }
          }
          label {
            display: flex;
            justify-content: center;
          }
        }

        .app-logo {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-direction: column;
          margin-left: 20px;
          .app-img {
            margin-top: 5px;
            height: 60px;
            width: 60px;
            border-radius: 50%;
            border: 1px solid #ededed;
          }
          label {
            margin-top: 5px;
          }
        }
      }

      .company-info {
        display: flex;
        flex-direction: column;
        margin-left: 20px;
        justify-content: center;
        margin-bottom: 6px;

        .company-name {
          display: inline-block;
          vertical-align: bottom;
          margin: 0 20px -2px 0;
          font-size: 20px;
          font-weight: bold;
        }

        .status {
          margin-top: 20px;
          color: #fff;
          padding-left: 3px;
          vertical-align: top;
          display: inline-block;
          width: 82px;
          height: 22px;
          line-height: 22px;
          font-size: 14px;
          font-weight: 500;
        }

        .pending-status {
          background: url('~@/assets/image/companyApproval/img_pending.png') no-repeat center;
          background-size: 100% 100%;
        }

        .new-status {
          background: url('~@/assets/image/companyApproval/img_new.png') no-repeat center;
          background-size: 100% 100%;
        }

        .rejected-status {
          background: url('~@/assets/image/companyApproval/img_reject.png') no-repeat center;
          background-size: 100% 100%;
        }

        .approved-status {
          background: url('~@/assets/image/companyApproval/img_approve.png') no-repeat center;
          background-size: 100% 100%;
        }

        .closed-status {
          background: url('~@/assets/image/companyApproval/img_close.png') no-repeat center;
          background-size: 100% 100%;
        }

      }
    }

    .content-container {
      margin-top: 24px;
      display: flex;
      justify-content: space-between;

      .profile-info {
        flex: 1;

        .basic-info {
          .description {
            ::v-deep p {
              margin: 0;
              padding: 0;
            }
          }
        }

        .business-license {
          .business-license-row {
            width: 150px;
            height: 100px;
            border: 2px solid #666;
            a {
              width: 100%;
              height: 100%;
              cursor: pointer;
              color: #0099cc;
              display: flex;
              justify-content: center;
              .business-license-img {
                max-width: 100%;
                max-height: 100%;
              }
            }
          }
        }

        .contact-info {
          a {
            cursor: pointer;
            color: #0099cc;
          }
        }

        .item-info-left {
          margin-right: 12px;
          margin-bottom: 24px;
        }

        .item-info-right {
          margin-left: 12px;
          margin-bottom: 24px;
        }
      }
    }

    .bottom-btn-group {
      display: flex;
      justify-content: space-between;
      margin: 45px auto 0;

      .el-button {
        padding: 0px 20px;
        border-radius: 2px;
        height: 28px;
        font-size: 14px;
        font-weight: 500;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }

      .status-btn {
        margin-left: 20px;
      }
    }
  }
}
</style>
